import { Box, Stack } from '@mui/material'
import { TaskTypeLabelWithIcon, TinyButton, tokens, User } from 'ComponentLibrary'
import { Task } from 'ComponentLibrary/src/common/tasks'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react'
import { ComponentProps, ReactElement, ReactNode, useState } from 'react'
import {
    TaskAssigneeSelection,
    TaskSelectionOnChange,
} from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskDetails/TaskAssigneeSelection'
import { isEmpty } from 'lodash'
import { DateWithIcon } from 'ComponentLibrary/src/molecules/DateWithIcon/DateWithIcon'
import useElementOverflow from 'ComponentLibrary/src/hooks/useElementOverflow'
import TaskVisibilityIcon from 'ComponentLibrary/src/molecules/TaskVisibilityIcon/TaskVisibilityIcon'
import { TaskAssignee } from 'ComponentLibrary/src/common/users'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type TaskDetailsProps = {
    task: Pick<Task, 'description' | 'due_date' | 'has_limited_visibility'>
    selectedAssignee: TaskAssignee | null
    assigneeOptions: User[]
    onChangeAssignee: TaskSelectionOnChange
    actions?: Action[]
    hasVisibilityField?: boolean
    copy?: Partial<TaskDetailsCopy>
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

type Action = {
    icon: ReactElement
    onClick: () => void
}

export type TaskDetailsCopy = {
    title: string
    assignee: string
    customerVisibility: string
    hidden: string
    visible: string
    taskType: string
    deadline: string
    notSet: string
    description: string
    showLess: string
    showMore: string
}

const DEFAULT_TASK_DETAILS_COPY: TaskDetailsCopy = {
    title: 'Aufgabendetails',
    assignee: 'Zuständig',
    customerVisibility: 'Sichtbarkeit für Kunden',
    hidden: 'Versteckt',
    visible: 'Sichtbar',
    taskType: 'Aufgabentyp',
    deadline: 'Deadline',
    notSet: 'nicht festgelegt',
    description: 'Beschreibung',
    showLess: 'Weniger anzeigen',
    showMore: 'Mehr anzeigen',
}

export function TaskDetails({
    task,
    actions = [],
    assigneeOptions,
    selectedAssignee,
    onChangeAssignee,
    hasVisibilityField,
    copy,
    userLinkCopy,
    locale = 'de-DE',
}: TaskDetailsProps) {
    const resolvedCopy = { ...DEFAULT_TASK_DETAILS_COPY, ...copy }
    return (
        <Stack
            padding={`${tokens.SizingSpacing200} ${tokens.SizingSpacing300} 0 ${tokens.SizingSpacing300}`}
            height="100%"
            width="100%"
            boxSizing="border-box"
            gap={tokens.SizingSpacing200}
        >
            <Stack direction="row" height={tokens.SizingSpacing300}>
                <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                    {resolvedCopy.title}
                </Typography>
                <Box marginLeft="auto" marginTop="-3px">
                    {actions.map((action, index) => {
                        return <IconButton key={index} variant="secondary" icon={action.icon} onClick={action.onClick} size="small" />
                    })}
                </Box>
            </Stack>
            <Stack sx={{ overflowY: 'auto' }} gap={tokens.SizingSpacing200}>
                <Stack gap={tokens.SizingSpacing050}>
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {resolvedCopy.assignee}
                    </Typography>
                    <TaskAssigneeSelection
                        assignee={selectedAssignee}
                        options={assigneeOptions}
                        onChangeAssignee={onChangeAssignee}
                        userLinkCopy={userLinkCopy}
                    />
                </Stack>
                {hasVisibilityField && (
                    <Stack gap={tokens.SizingSpacing050}>
                        <Typography variant="textS" color={tokens.ColorTextSubtle}>
                            {resolvedCopy.customerVisibility}
                        </Typography>
                        <Stack gap={tokens.SizingSpacing075} alignItems="center" direction="row">
                            <TaskVisibilityIcon isLimitedVisibility={!!task.has_limited_visibility} size="small" />
                            <Typography variant="textM" color={tokens.ColorTextBold}>
                                {task.has_limited_visibility ? resolvedCopy.hidden : resolvedCopy.visible}
                            </Typography>
                        </Stack>
                    </Stack>
                )}
                <Stack direction="row" alignSelf="stretch" gap={tokens.SizingSpacing150} width="100%">
                    <Stack gap={tokens.SizingSpacing050} flex="1 0 0">
                        <Typography variant="textS" color={tokens.ColorTextSubtle}>
                            {resolvedCopy.taskType}
                        </Typography>
                        <TaskTypeLabelWithIcon taskType="todo" />
                    </Stack>
                    <Stack gap={tokens.SizingSpacing050} flex="1 0 0">
                        <Typography variant="textS" color={tokens.ColorTextSubtle}>
                            {resolvedCopy.deadline}
                        </Typography>
                        {task.due_date ? (
                            <DateWithIcon date={task.due_date} locale={locale} />
                        ) : (
                            <Typography variant="textM" color={tokens.ColorTextDisabled}>
                                {resolvedCopy.notSet}
                            </Typography>
                        )}
                    </Stack>
                </Stack>
                <Stack gap={tokens.SizingSpacing050} flex="1 0 0">
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {resolvedCopy.description}
                    </Typography>
                    <TypographyWithShowMore lineClamp={4} showMoreLabel={resolvedCopy.showMore} showLessLabel={resolvedCopy.showLess}>
                        {isEmpty(task.description) ? '-' : task.description}
                    </TypographyWithShowMore>
                </Stack>
            </Stack>
        </Stack>
    )
}

type TypographyWithShowMoreProps = {
    children: ReactNode
    lineClamp: ComponentProps<typeof Typography>['lineClamp']
    showMoreLabel: string
    showLessLabel: string
}

function TypographyWithShowMore({ children, lineClamp = 2, showMoreLabel, showLessLabel }: TypographyWithShowMoreProps) {
    const [isExpanded, setIsExpanded] = useState(false)
    const toggleIsExpanded = () => setIsExpanded((prev) => !prev)
    const { elementRef, isOverflowing } = useElementOverflow({ verticalOverflow: true })

    return (
        <Stack>
            <Typography ref={elementRef} variant="textM" color={tokens.ColorTextBold} lineClamp={isExpanded ? undefined : lineClamp}>
                {children}
            </Typography>
            {(isOverflowing || isExpanded) && (
                <Stack alignSelf="center" margin={`${tokens.SizingSpacing200}`}>
                    <TinyButton
                        onClick={toggleIsExpanded}
                        rightIcon={isExpanded ? <CaretUpIcon /> : <CaretDownIcon />}
                        label={isExpanded ? showLessLabel : showMoreLabel}
                    />
                </Stack>
            )}
        </Stack>
    )
}
